import cn from 'classnames';

import { useDictionary } from 'src/hooks/useDictionary';

import { formatEmail } from 'src/utils/formatEmail';
import { formatPhone } from 'src/utils/formatPhone';

import Cta from 'components/BaseHelpers/Cta';
import Link from 'components/BaseHelpers/Link';
import LinkOrElement from 'components/BaseHelpers/LinkOrElement';
import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';

import { ComponentProps } from '../../../types/component-props';
import { CoreLocationDetailsData } from '../../../types/components';

import styles from './CoreLocationDetails.module.scss';

export type CoreLocationDetailsProps = ComponentProps & CoreLocationDetailsData;

const CoreLocationDetails = (props: CoreLocationDetailsProps): JSX.Element => {
  const { phone, fax, address, googleMapsUrl, directionDetails, contact } = props.fields;
  const {
    phoneLabel,
    faxLabel,
    emailLabel,
    contactInformationLabel,
    addressLabel,
    directionDetailsLabel,
    getDirectionsLabel,
    officeContactLabel,
  } = useDictionary();

  if (
    !phone &&
    !fax &&
    !address &&
    !googleMapsUrl &&
    !directionDetails &&
    !contact.name &&
    !contact.email
  ) {
    return <></>;
  }

  const phoneFormatted = formatPhone(phone);
  const formattedEmail = formatEmail(contact.email);

  return (
    <div className={styles.main}>
      {Boolean(phone || fax || address || googleMapsUrl || directionDetails) && (
        <div className={styles['contact-information']}>
          <Text tag="h2" field={contactInformationLabel} className={cn('heading-h4')} />
          {Boolean(phoneFormatted || fax || address) && (
            <div className={styles['number-address']}>
              {phoneFormatted && (
                <div>
                  <Text field={phoneLabel} className={cn(styles['subhead'], 'body-med')} />
                  <Link href={phoneFormatted.href} className={cn(styles['value'], 'body-med')}>
                    {phoneFormatted.text}
                  </Link>
                </div>
              )}
              {fax && (
                <div>
                  <Text field={faxLabel} className={cn(styles['subhead'], 'body-med')} />
                  <Text field={fax} className={cn(styles['value'], 'body-med')} />
                </div>
              )}
              {address && (
                <div>
                  <Text
                    field={addressLabel}
                    className={cn(styles['subhead'], styles['subhead-block'], 'body-med')}
                  />
                  <RichText
                    field={address}
                    className={cn(styles['value'], 'body-med')}
                    styled={false}
                  />
                </div>
              )}
            </div>
          )}
          {googleMapsUrl && (
            <Cta tag="a" href={googleMapsUrl} openNewTab={true}>
              {getDirectionsLabel}
            </Cta>
          )}
          {directionDetails && (
            <div>
              <Text
                field={directionDetailsLabel}
                className={cn(styles['subhead'], styles['subhead-block'], 'body-med')}
              />
              <RichText
                field={directionDetails}
                className={cn(styles['value'], 'body-med')}
                styled={false}
              />
            </div>
          )}
        </div>
      )}
      {Boolean(contact.name || contact.email) && (
        <div className={styles['office-contact']}>
          <Text tag="h2" field={officeContactLabel} className={cn('heading-h4')} />
          <div className={styles['contact-details']}>
            {contact.name && (
              <LinkOrElement href={contact.url} className={cn(styles['contact-name'], 'body-lg')}>
                {contact.name}
              </LinkOrElement>
            )}
            {formattedEmail && (
              <div>
                <Text field={emailLabel} className={cn(styles['subhead'], 'body-med')} />
                <Link
                  href={formattedEmail.href}
                  className={cn(styles['value'], 'body-med', styles['email'])}
                >
                  <span dangerouslySetInnerHTML={{ __html: formattedEmail.emailHtml }} />
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CoreLocationDetails;
